import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Web3 from 'web3';
import { DollarSign, TrendingUp, BarChart2, Clock, AlertTriangle, Check, Calendar, Users, Link } from 'lucide-react';
import EscrowContractABI from './EscrowContractABI.json';
import USDTContractABI from './USDTContractABI.json';
import './ListingDetailsPage.css';
import Popup from './Popup';

const web3 = new Web3(window.ethereum);
const escrowContractAddress = '0xB7d3164045A77F18165A748a15aa02A85aF70A27';
const usdtContractAddress = '0x0aa347912773a1f7096cAb7Ab6Bb8043B0B7B5C9';

const escrowContract = new web3.eth.Contract(EscrowContractABI, escrowContractAddress);
const usdtContract = new web3.eth.Contract(USDTContractABI, usdtContractAddress);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function bigIntToString(obj) {
  return JSON.parse(JSON.stringify(obj, (key, value) =>
    typeof value === 'bigint' ? value.toString() : value
  ));
}

function ListingDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [userAddress, setUserAddress] = useState('');
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [userTransaction, setUserTransaction] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [listingAvailable, setListingAvailable] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [telegramBotLink, setTelegramBotLink] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const accounts = await web3.eth.getAccounts();
      const address = accounts[0];
      setUserAddress(address);

      const listingResponse = await axios.get(`${API_URL}/api/listings/${id}`);
      setListing(listingResponse.data);

      const transactionsResponse = await axios.get(`${API_URL}/api/listings/${id}/pending-transactions`);
      const transactions = transactionsResponse.data;

      let activeTransactionExists = false;
      let userDisputeExists = false;
      const updatedTransactions = await Promise.all(transactions.map(async (tx) => {
        const blockchainTx = await escrowContract.methods.transactions(tx.transactionId).call();
        const updatedTx = {
          ...tx,
          blockchainStatus: blockchainTx.isComplete ? 'Completed' :
            blockchainTx.isDisputed ? 'Disputed' :
              blockchainTx.isWithdrawn ? 'Withdrawn' : 'Pending'
        };
        if (updatedTx.blockchainStatus === 'Pending' || updatedTx.blockchainStatus === 'Disputed') {
          activeTransactionExists = true;
          if (updatedTx.buyer.toLowerCase() === address.toLowerCase() && updatedTx.blockchainStatus === 'Disputed') {
            userDisputeExists = true;
          }
        }
        return updatedTx;
      }));

      setPendingTransactions(updatedTransactions);
      setListingAvailable(!activeTransactionExists && listingResponse.data.status !== 'Sold');

      if (address) {
        const userTx = updatedTransactions.find(tx =>
          tx.buyer &&
          tx.buyer.toLowerCase() === address.toLowerCase() &&
          (tx.blockchainStatus === 'Pending' || tx.blockchainStatus === 'Disputed')
        );
        setUserTransaction(userTx || null);
      } else {
        setUserTransaction(null);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'An error occurred while fetching data');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let timer;
    if (userTransaction && (userTransaction.status === 'pending' || userTransaction.status === 'active')) {
      timer = setInterval(async () => {
        try {
          const transactionId = userTransaction.transactionId;
          const txDetails = await escrowContract.methods.transactions(transactionId).call();
          const now = Math.floor(Date.now() / 1000);
          const timeLimit = await escrowContract.methods.defaultTimeLimit().call();
          const expiresAt = parseInt(txDetails.createdAt) + parseInt(timeLimit);
          const timeLeft = expiresAt - now;

          if (timeLeft <= 0) {
            setRemainingTime('Expired');
            setIsExpired(true);
            clearInterval(timer);
          } else {
            const hours = Math.floor(timeLeft / 3600);
            const minutes = Math.floor((timeLeft % 3600) / 60);
            const seconds = timeLeft % 60;
            setRemainingTime(`${hours}h ${minutes}m ${seconds}s`);
            setIsExpired(false);
          }
        } catch (error) {
          console.error('Error checking transaction status:', error);
        }
      }, 1000);
    } else {
      setRemainingTime(null);
      setIsExpired(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [userTransaction, escrowContract.methods]);


  const showPopupMessage = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  };

  const startTransaction = async (seller, price) => {
    const accounts = await web3.eth.getAccounts();
    const buyer = accounts[0];

    const priceInWei = web3.utils.toWei(price.toString(), 'ether');

    try {
      console.log('Approving USDT transfer...');
      const approvalTx = await usdtContract.methods.approve(escrowContractAddress, priceInWei).send({ from: buyer });
      console.log('USDT approval result:', bigIntToString(approvalTx));

      if (!approvalTx.status) {
        throw new Error('USDT approval failed');
      }

      console.log('Starting escrow transaction...');
      const result = await escrowContract.methods.startTransaction(seller, priceInWei).send({ from: buyer });

      console.log('Blockchain transaction result:', bigIntToString(result));

      const blockchainTransactionHash = result.transactionHash;
      const transactionId = result.events.TransactionStarted.returnValues.transactionId;

      const response = await axios.post(`${API_URL}/api/transactions`, {
        listingId: listing._id,
        buyer: buyer,
        blockchainTransactionHash: blockchainTransactionHash,
        transactionId: transactionId.toString()
      });

      console.log('Database transaction created:', response.data);

      showPopupMessage('Purchase successful! Transaction is pending.');
      setUserTransaction(response.data);
      fetchData(); // Refresh the listing data
    } catch (error) {
      console.error('Error during transaction:', error);
      showPopupMessage('Transaction failed: ' + error.message);
    }
  };

  const handleBuyClick = async () => {
    if (!listing) return;
    try {
      const accounts = await web3.eth.getAccounts();
      const buyer = accounts[0];

      const priceInWei = web3.utils.toWei(listing.price.toString(), 'ether');

      console.log('Approving USDT transfer...');
      const approvalTx = await usdtContract.methods.approve(escrowContractAddress, priceInWei).send({ from: buyer });
      console.log('USDT approval result:', bigIntToString(approvalTx));

      if (!approvalTx.status) {
        throw new Error('USDT approval failed');
      }

      console.log('Starting escrow transaction...');
      const result = await escrowContract.methods.startTransaction(listing.seller, priceInWei).send({ from: buyer });

      console.log('Blockchain transaction result:', bigIntToString(result));

      const blockchainTransactionHash = result.transactionHash;
      const transactionId = result.events.TransactionStarted.returnValues.transactionId;

      const transactionData = {
        listingId: listing._id,
        buyer: buyer,
        blockchainTransactionHash: blockchainTransactionHash,
        transactionId: transactionId.toString()
      };

      console.log('Sending transaction data to backend:', transactionData);

      const response = await axios.post(`${API_URL}/api/transactions`, transactionData);

      console.log('Database transaction created:', response.data);

      setTelegramBotLink(response.data.telegramBotLink);
      showPopupMessage(`Purchase initiated! Please use the Telegram bot to communicate with the seller. Your transaction ID is: ${transactionId}`);
      setUserTransaction(response.data);
      fetchData(); // Refresh the listing data
    } catch (error) {
      console.error('Error during purchase:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      }
      showPopupMessage('Purchase failed. Please check the console for more details.');
    }
  };

  const refreshData = async () => {
    try {
      await fetchData();
    } catch (error) {
      console.error('Error refreshing data:', error);
      showPopupMessage('Failed to refresh data. Please try again.');
    }
  };

  const handleWithdraw = async () => {
    if (!escrowContract || !userAddress || !userTransaction) {
      console.error('Escrow contract, user address, or user transaction not available');
      return;
    }
    console.log('Attempting to withdraw transaction:', userTransaction);
    try {
      if (!userTransaction.transactionId) {
        throw new Error('Transaction ID is missing');
      }

      console.log('Calling withdrawTransaction with ID:', userTransaction.transactionId);
      const withdrawResult = await escrowContract.methods.withdrawTransaction(userTransaction.transactionId).send({ from: userAddress });
      console.log('Withdraw transaction result:', withdrawResult);

      // Update the backend
      const response = await axios.post(`${API_URL}/api/transactions/${userTransaction.transactionId}/update-status`, {
        status: 'withdrawn'
      });

      console.log('Backend update response:', response.data);

      showPopupMessage('Transaction withdrawn successfully! The listing is now Available.');

      // Refresh the data
      await refreshData();

      // Navigate back to marketplace
      navigate('/marketplace');

    } catch (error) {
      console.error('Error withdrawing transaction:', error);
      showPopupMessage(`Failed to withdraw transaction: ${error.message}`);
    }
  };

  const handleDispute = async () => {
    try {
      const transactionId = userTransaction.transactionId;
      await escrowContract.methods.disputeTransaction(transactionId).send({ from: userAddress });
      showPopupMessage('Dispute raised successfully!');
      await fetchData(); // Refresh the listing data
    } catch (error) {
      console.error('Error raising dispute:', error);
      showPopupMessage(`Failed to raise dispute: ${error.message}`);
    }
  };

  if (isLoading) {
    return <div className="listing-details-page loading"></div>;
  }

  if (error) {
    return <div className="listing-details-page error">Error: {error}</div>;
  }

  if (!listing) {
    return <div className="listing-details-page not-found">Listing not found.</div>;
  }

  const isUserSeller = userAddress && listing.seller && userAddress.toLowerCase() === listing.seller.toLowerCase();
  const canBuy = listingAvailable && !isUserSeller && (!userTransaction || userTransaction.blockchainStatus === 'Withdrawn');

  return (
    <div className="listing-details-page">
      <div className="listing-details-container">
        <div className="listing-header">
          <h1>{listing.title}</h1>
          <div className={`status ${listing.status.toLowerCase()}`}>{listing.status}</div>
        </div>
        <div className="listing-body">
          <div className="listing-info">
            <p className="description">{listing.description}</p>
            <div className="price">
              <DollarSign />
              <span>{listing.price} USDT</span>
            </div>
            <div className="metrics">
              <div className="metric">
                <TrendingUp />
                <span>TTM Revenue: {listing.ttmRevenue} USDT</span>
              </div>
              <div className="metric">
                <BarChart2 />
                <span>TTM Profit: {listing.ttmProfit} USDT</span>
              </div>
              <div className="metric">
                <Calendar />
                <span>Last Month Revenue: {listing.lastMonthRevenue} USDT</span>
              </div>
              <div className="metric">
                <Calendar />
                <span>Last Month Profit: {listing.lastMonthProfit} USDT</span>
              </div>
              <div className="metric">
                <Users />
                <span>Team Size: {listing.teamSize}</span>
              </div>
              <div className="metric">
                <Link />
                <span>Website: <a href={listing.websiteUrl} target="_blank" rel="noopener noreferrer">{listing.websiteUrl}</a></span>
              </div>
            </div>
            <div className="additional-info">
              <h3>Asking Price Reasoning</h3>
              <p>{listing.askingPriceReasoning}</p>
            </div>
            <div className="additional-info">
              <h3>Key Metrics</h3>
              <p>{listing.keyMetrics}</p>
            </div>
          </div>
          <div className="listing-actions">
            {isUserSeller ? (
              <div className="info-message">
                <Check />
                <span>You are the seller of this listing.</span>
              </div>
            ) : listing.status === 'Sold' ? (
              <div className="info-message">
                <AlertTriangle />
                <span>This listing has been sold.</span>
              </div>
            ) : userTransaction ? (
              <div className="transaction-info">
                <h3>Your Transaction</h3>
                {userTransaction.status === 'pending' || userTransaction.status === 'active' ? (
                  <>
                    <p>Transaction ID: {userTransaction.transactionId}</p>
                    <p>Remaining Time: {remainingTime}</p>
                    <p>Use this link to chat with the seller: <a href={`https://t.me/codexchangebot`} target="_blank" rel="noopener noreferrer">https://t.me/codexchangebot</a></p>
                    {!isExpired ? (
                      <button className="action-button dispute" onClick={handleDispute}>Raise Dispute</button>
                    ) : (
                      <button className="action-button withdraw" onClick={handleWithdraw}>Withdraw</button>
                    )}
                  </>
                ) : (
                  <p>Transaction status: {userTransaction.status}</p>
                )}
              </div>
            ) : canBuy ? (
              <button className="action-button buy" onClick={handleBuyClick}>
                Buy Now
              </button>
            ) : (
              <div className="info-message">
                <AlertTriangle />
                <span>This listing is not available for purchase at the moment.</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {showPopup && (
        <Popup
          message={popupMessage}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}

export default ListingDetailsPage;